import { useState } from "react";
import { Modal, Button, FormField, Input, Badge, ErrorState } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { useScanOrdonnance } from "../lib/api";
import { CAPS } from "../lib/caps";
import type { ScanResult } from "../lib/types";
import { formatMontant } from "../lib/format";
import { Mono } from "./Mono";

export interface ScanOrdonnanceModalProps {
  open: boolean;
  onClose: () => void;
  /** Hand the matched, priced lines (their ParametreLabo ids) to the basket. */
  onSeed: (parametreLaboIds: Array<string | number>, result: ScanResult) => void;
}

/**
 * ScanOrdonnanceModal — Rx-QR intake (EB-01). DARK day-1: the adapter throws RX_INTAKE_UNAVAILABLE
 * (501) and we surface "scan indisponible — saisie manuelle". When the core lands, the matched lines
 * seed the basket; unmatched lines are SHOWN, never auto-added (§3.3).
 */
export function ScanOrdonnanceModal({ open, onClose, onSeed }: ScanOrdonnanceModalProps) {
  const { t } = useT();
  const canScan = useCan(CAPS.ordonnanceScan);
  const [code, setCode] = useState("");
  const scan = useScanOrdonnance();
  const result = scan.data;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    scan.mutate({ raw_token_or_code: code.trim() });
  };

  const seed = () => {
    if (!result) return;
    onSeed(
      result.lignes.map((l) => l.parametre_labo_id),
      result,
    );
    onClose();
  };

  // 501 (dark adapter) is the expected day-1 state — surface the manual-entry guidance, not an error.
  const unavailable = scan.isError && scan.error?.httpStatus === 501;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("reception.scan.title")}
      footer={
        result ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("reception.form.cancel")}
            </Button>
            <Button onClick={seed} disabled={result.lignes.length === 0}>
              {t("reception.scan.seed")}
            </Button>
          </>
        ) : undefined
      }
    >
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        <FormField label={t("reception.scan.label")}>
          {(p) => (
            <Input
              {...p}
              mono
              autoFocus
              placeholder={t("reception.scan.placeholder")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={!canScan}
            />
          )}
        </FormField>

        {!result && (
          <Button type="submit" loading={scan.isPending} disabled={!canScan || !code.trim()}>
            {t("reception.scan.submit")}
          </Button>
        )}
      </form>

      {unavailable && (
        <p
          role="status"
          style={{
            marginBlockStart: token.s4,
            padding: token.s3,
            background: token.tintHold,
            color: token.hold,
            borderRadius: token.rSm,
          }}
        >
          ❚❚ {t("reception.scan.unavailable")}
        </p>
      )}

      {scan.isError && !unavailable && (
        <div style={{ marginBlockStart: token.s4 }}>
          <ErrorState
            message={scan.error?.message ?? t("reception.error.generic")}
            code={scan.error?.code}
          />
        </div>
      )}

      {result && (
        <div style={{ marginBlockStart: token.s4, display: "flex", flexDirection: "column", gap: token.s4 }}>
          <section>
            <h3 style={{ fontSize: token.fsLabel, margin: 0, marginBlockEnd: token.s2 }}>
              {t("reception.scan.matched")}
            </h3>
            <ul style={{ margin: 0, paddingInlineStart: token.s5 }}>
              {result.lignes.map((l) => (
                <li key={String(l.parametre_labo_id)}>
                  <Mono>{l.parametre_code}</Mono> — {l.libelle} ·{" "}
                  <Mono>{formatMontant(l.prix)}</Mono>
                </li>
              ))}
            </ul>
          </section>
          {result.lignes_non_mappees.length > 0 && (
            <section>
              <h3 style={{ fontSize: token.fsLabel, margin: 0, marginBlockEnd: token.s2 }}>
                {t("reception.scan.unmatched")} <Badge tone="warn">{result.lignes_non_mappees.length}</Badge>
              </h3>
              <ul style={{ margin: 0, paddingInlineStart: token.s5, color: token.slate }}>
                {result.lignes_non_mappees.map((l) => (
                  <li key={l.analyse_code}>
                    <Mono>{l.analyse_code}</Mono>
                    {l.libelle ? ` — ${l.libelle}` : ""}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
